import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Map, 
  CheckSquare, 
  FileText, 
  AppWindow, 
  Newspaper,
  LogOut,
  Menu,
  X,
  BookOpen,
  Settings,
  BarChart3,
  ChevronDown,
  Rocket,
  Palette,
  Users
} from 'lucide-react';

const PortalLayout = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [springboardOpen, setSpringboardOpen] = useState(true);
  const [branding, setBranding] = useState(null);

  // Fetch branding on mount
  useEffect(() => {
    const fetchBranding = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/branding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.branding) setBranding(res.data.branding);
      } catch {}
    };
    fetchBranding();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Derive portal accent colors from branding
  const accent = branding?.accentColour || '#F6ADBC';
  const primary = branding?.primaryColour || '#1a1a1a';
  const fontFamily = branding?.fontFamily || 'Inter, sans-serif';
  const brandName = branding?.businessName || user?.business_name || null;
  const isDefaultBrand = !brandName;

  // Journey items
  const journeyItems = [
    { to: '/portal', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/portal/stages', icon: Map, label: 'My Journey' },
    { to: '/portal/programme', icon: BookOpen, label: 'Programme Details' },
  ];

  // Springboard items (dropdown)
  const springboardItems = [
    { to: '/portal/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/portal/documents', icon: FileText, label: 'Documents' },
    { to: '/portal/apps', icon: AppWindow, label: 'Business Apps' },
    { to: '/portal/business-dashboard', icon: BarChart3, label: 'Business Dashboard' },
    { to: '/portal/team', icon: Users, label: 'Team' },
    { to: '/portal/branding', icon: Palette, label: 'Branding' },
  ];

  // Other items
  const otherItems = [
    { to: '/portal/news', icon: Newspaper, label: 'News' },
    { to: '/portal/settings', icon: Settings, label: 'Settings' },
  ];

  const isSpringboardActive = springboardItems.some(item => location.pathname === item.to);

  const NavItem = ({ to, icon: Icon, label, end, springboard }) => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-none transition-all text-sm ${
          isActive
            ? springboard 
              ? 'bg-amber-100 text-amber-900 font-semibold'
              : 'bg-primary text-white font-semibold'
            : springboard
              ? 'text-amber-800/60 hover:bg-amber-50 hover:text-amber-900'
              : 'text-black/60 hover:bg-secondary hover:text-black'
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </NavLink>
  );

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-border">
        <button 
          onClick={() => navigate('/')}
          className="text-left group"
          data-testid="portal-home-link"
        >
          <h1 className="text-2xl font-bold group-hover:opacity-80 transition-colors" style={{ color: primary, fontFamily: isDefaultBrand ? "var(--font)" : fontFamily, letterSpacing: isDefaultBrand ? '-1.5px' : undefined }}>
            {isDefaultBrand ? (
              <>spring<span style={{ display: 'inline-block', width: 7, height: 7, background: '#F6ADBC', borderRadius: '50%', marginLeft: 1, verticalAlign: 'baseline', position: 'relative', top: -2 }} /></>
            ) : brandName}
          </h1>
          <p className="text-xs text-black/40 mt-1 group-hover:text-black/60 transition-colors">Back to Home</p>
        </button>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {user?.avatar ? (
            <img src={user.avatar} alt="" className="w-9 h-9 rounded-none" data-testid="sidebar-avatar" />
          ) : (
            <div className="w-9 h-9 rounded-none flex items-center justify-center" style={{ background: accent }} data-testid="sidebar-avatar-fallback">
              <span className="font-bold text-sm" style={{ color: primary }}>{user?.name?.charAt(0) || 'U'}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-black text-sm truncate">{user?.name}</p>
            <p className="text-xs text-black/40 truncate">
              {user?.business_name || 'Setting up...'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pt-4">
        {/* Journey Section */}
        <div className="mb-2">
          <p className="px-4 text-[10px] font-bold uppercase tracking-widest text-black/35 mb-2">Journey</p>
          <div className="space-y-0.5">
            {journeyItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="my-4 border-t border-border" />

        {/* Springboard Section — uses branding accent */}
        <div className="mb-2">
          <button
            onClick={() => setSpringboardOpen(!springboardOpen)}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-none transition-all"
            style={{ background: isSpringboardActive ? `${accent}22` : 'transparent' }}
            data-testid="nav-springboard-toggle"
          >
            <Rocket className="h-4 w-4" style={{ color: primary }} />
            <span className="text-sm font-bold flex-1 text-left tracking-tight" style={{ color: primary }}>Springboard</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${springboardOpen ? 'rotate-180' : ''}`} style={{ color: primary }} />
          </button>
          
          {springboardOpen && (
            <div className="mt-1 ml-2 pl-3 space-y-0.5" style={{ borderLeft: `2px solid ${accent}44` }}>
              {springboardItems.map((item) => (
                <NavItem key={item.to} {...item} springboard />
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-4 border-t border-border" />

        {/* Other items */}
        <div className="space-y-0.5">
          {otherItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-border">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2.5 w-full text-black/50 hover:text-black hover:bg-secondary rounded-none transition-all text-sm"
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-secondary/30 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 bg-white border-r border-border fixed h-full">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-border z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-black/60 hover:text-black"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <button 
            onClick={() => navigate('/')}
            className="text-xl font-bold text-black hover:text-primary transition-colors tracking-tight"
          >
            SPRING.
          </button>
          <div className="w-10" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-72 bg-white z-50 transform transition-transform flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <button 
            onClick={() => { setSidebarOpen(false); navigate('/'); }}
            className="text-xl font-bold text-black hover:text-primary transition-colors tracking-tight"
          >
            SPRING.
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-black/60 hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-60 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Toaster handled globally */}
    </div>
  );
};

export default PortalLayout;
